import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCopy, ExternalLink, RefreshCw, Check, X } from "lucide-react";

const API = `${window.location.origin}/api`;
const GOLD = "#C4A671";

const FILTERS = [
  ["new", "Da presidiare"],
  ["replied", "Risposti"],
  ["dismissed", "Scartati"],
];

function RelevanceBadge({ value }) {
  if (value == null) return <span className="text-[11px] text-[#5a5548]">—</span>;
  const color = value >= 80 ? GOLD : value >= 60 ? "#C9C3B5" : "#5a5548";
  return (
    <div className="text-center shrink-0 w-14">
      <div className="font-serif-display text-[26px] leading-none" style={{ color }}>{value}</div>
      <div className="text-[9px] text-[#5a5548] mt-0.5" style={{ letterSpacing: "0.05em" }}>/100</div>
    </div>
  );
}

function ThreadCard({ t, onPatch, onRedraft, onToRadar, busyId }) {
  const copy = async () => {
    await navigator.clipboard.writeText(t.draft);
    toast.success("Bozza copiata — pronta da incollare su Reddit.");
  };
  const age = t.created_utc
    ? new Date(t.created_utc * 1000).toLocaleDateString("it-IT")
    : "";
  return (
    <div className="border border-[#1f1f1f] bg-[#0F0E0B] p-5" data-testid={`sentinel-thread-${t.id}`}>
      <div className="flex gap-4">
        <RelevanceBadge value={t.relevance} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="overline text-[8.5px] px-2 py-0.5 border border-[#C4A671]/40" style={{ color: GOLD }}>
              r/{t.subreddit}
            </span>
            {t.b2b && (
              <span
                data-testid={`sentinel-b2b-${t.id}`}
                className="overline text-[8.5px] px-2 py-0.5 border"
                style={{ color: "#E8B923", borderColor: "rgba(232,185,35,0.55)", background: "rgba(232,185,35,0.08)" }}
              >
                🔥 Opportunità B2B
              </span>
            )}
            <span className="text-[10.5px] text-[#5a5548]">
              {t.score != null && <>▲ {t.score} · {t.num_comments} commenti · </>}{age} · u/{t.author}
            </span>
            {t.lang && <span className="text-[10px] uppercase text-[#7A7568]">{t.lang}</span>}
          </div>
          <a
            href={t.permalink} target="_blank" rel="noreferrer"
            data-testid={`sentinel-link-${t.id}`}
            className="inline-flex items-start gap-1.5 font-serif-display text-[16.5px] leading-snug text-[#F2EFE8] hover:text-[#C4A671] transition"
          >
            {t.title} <ExternalLink size={13} className="mt-1 shrink-0 opacity-50" />
          </a>
          {t.motivo && (
            <p className="mt-1.5 text-[12px] italic text-[#8B7A55]">{t.motivo}</p>
          )}
          {t.draft ? (
            <div className="mt-3 border border-[#C4A671]/25 bg-[#0A0A0A] p-3.5">
              <div className="flex items-center justify-between mb-2">
                <span className="overline text-[8.5px]" style={{ color: GOLD }}>Bozza pronta</span>
                <button
                  onClick={copy}
                  data-testid={`sentinel-copy-${t.id}`}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#7A7568] hover:text-[#C4A671] transition"
                >
                  <ClipboardCopy size={12} /> Copia Testo
                </button>
              </div>
              <p className="text-[12.5px] leading-[1.75] text-[#C9C3B5] whitespace-pre-wrap">{t.draft}</p>
            </div>
          ) : (
            <p className="mt-2 text-[11.5px] text-[#5a5548]">
              Nessuna bozza (sotto soglia). Puoi generarla comunque con ↻.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {t.status === "new" && (
              <>
                <button
                  data-testid={`sentinel-replied-${t.id}`}
                  onClick={() => onPatch(t.id, "replied")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[9.5px] uppercase tracking-[0.18em] border border-[#C4A671]/50 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
                >
                  <Check size={12} /> Ho risposto
                </button>
                <button
                  data-testid={`sentinel-dismiss-${t.id}`}
                  onClick={() => onPatch(t.id, "dismissed")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[9.5px] uppercase tracking-[0.18em] border border-[#2a2a2a] text-[#7A7568] hover:text-[#e07b6d] hover:border-[#c0392b]/50 transition"
                >
                  <X size={12} /> Scarta
                </button>
              </>
            )}
            {t.status !== "new" && (
              <button
                onClick={() => onPatch(t.id, "new")}
                className="px-3 py-1.5 text-[9.5px] uppercase tracking-[0.18em] border border-[#2a2a2a] text-[#7A7568] hover:text-[#C9C3B5] transition"
              >
                ↩ Riporta in coda
              </button>
            )}
            <button
              data-testid={`sentinel-redraft-${t.id}`}
              onClick={() => onRedraft(t.id)}
              disabled={busyId === t.id}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[9.5px] uppercase tracking-[0.18em] border border-[#2a2a2a] text-[#7A7568] hover:text-[#C4A671] transition disabled:opacity-40"
            >
              <RefreshCw size={12} className={busyId === t.id ? "animate-spin" : ""} />
              {busyId === t.id ? "Rigenero…" : "Rigenera bozza"}
            </button>
            {t.radar_prospect_id ? (
              <span
                data-testid={`sentinel-in-radar-${t.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[9.5px] uppercase tracking-[0.18em] border border-[#C4A671]/25 text-[#8B7A55]"
              >
                ✓ Nel Radar
              </span>
            ) : (
              <button
                data-testid={`sentinel-to-radar-${t.id}`}
                onClick={() => onToRadar(t.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[9.5px] uppercase tracking-[0.18em] border transition"
                style={{ color: "#E8B923", borderColor: "rgba(232,185,35,0.5)" }}
              >
                ＋ Aggiungi al Radar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SentinelPanel({ authHeader }) {
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState("new");
  const [run, setRun] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadThreads = async (f = filter) => {
    try {
      const { data } = await axios.get(`${API}/admin/sentinel/threads?status=${f}`, authHeader);
      setThreads(data?.threads || []);
    } catch { /* silent */ }
  };
  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sentinel/status`, authHeader);
      setRun(data);
      return data;
    } catch { return null; }
  };
  const loadSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sentinel/settings`, authHeader);
      setSettings(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadThreads();
    loadStatus();
    loadSettings();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (run?.status !== "running") return;
    const iv = setInterval(async () => {
      const s = await loadStatus();
      if (s && s.status !== "running") {
        clearInterval(iv);
        toast.success(`Scansione conclusa: ${s.new} nuovi thread, ${s.drafted} bozze pronte.`);
        loadThreads();
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [run?.status]); // eslint-disable-line

  const startScan = async () => {
    try {
      await axios.post(`${API}/admin/sentinel/scan`, {}, authHeader);
      toast.info("Scansione avviata su r/wine, r/sommelier, r/FineDining, r/winemaking, r/Sicily…");
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Avvio scansione non riuscito.");
    }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await axios.post(`${API}/admin/sentinel/settings`, patch, authHeader);
      setSettings(data);
      toast.success("Impostazioni Sentinel salvate.");
    } catch { toast.error("Salvataggio non riuscito."); }
  };

  const patchThread = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/sentinel/threads/${id}`, { status }, authHeader);
      setThreads((p) => p.filter((x) => x.id !== id));
    } catch { toast.error("Aggiornamento non riuscito."); }
  };

  const redraft = async (id) => {
    setBusyId(id);
    try {
      const { data } = await axios.post(`${API}/admin/sentinel/threads/${id}/draft`, {}, authHeader);
      setThreads((p) => p.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Bozza rigenerata.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rigenerazione non riuscita.");
    } finally { setBusyId(null); }
  };

  const toRadar = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/sentinel/threads/${id}/to-radar`, {}, authHeader);
      setThreads((p) => p.map((x) => (x.id === id ? { ...x, radar_prospect_id: data.prospect_id } : x)));
      toast.success(`«${data.business_name}» aggiunto al Radar: lo trovi tra i prospect del CRM.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aggiunta al Radar non riuscita.");
    }
  };

  const running = run?.status === "running";

  return (
    <div className="max-w-5xl" data-testid="sentinel-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-serif-display text-[22px] text-[#F2EFE8] mb-1">🎯 Reddit Sentinel</h2>
          <p className="text-[13px] text-[#7A7568] max-w-xl">
            Presidio organico a 3 cerchi concentrici: keyword mirate su 11 subreddit di settore,
            poi query ampie (carte dei vini, pairing, ristorazione italiana) e infine i post più
            recenti delle community vino, finché il bottino non raggiunge il target. Ogni 6 ore,
            bozze autorevoli nella lingua del thread — tu le copi e intervieni dal tuo account.
          </p>
        </div>
        <button
          data-testid="sentinel-scan-now"
          onClick={startScan}
          disabled={running}
          className="bg-[#C4A671] text-[#0A0A0A] px-6 py-2.5 text-[10px] tracking-[0.22em] uppercase font-medium hover:bg-white transition disabled:opacity-40"
        >
          {running ? "Scansione in corso…" : "🛰 Scansiona ora"}
        </button>
      </div>

      {settings && (
        <div className="mt-5 pt-4 border-t border-[#1f1f1f] flex flex-wrap items-center gap-x-6 gap-y-3"
             data-testid="sentinel-settings">
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="sentinel-scheduled-toggle">
            <input
              type="checkbox"
              checked={!!settings.scheduled_enabled}
              onChange={(e) => saveSettings({ scheduled_enabled: e.target.checked })}
              className="accent-[#C4A671] w-4 h-4"
            />
            <span className="text-[11.5px] text-[#C9C3B5]">Scansione automatica H24 <span className="text-[#7A7568]">(ogni 6 ore)</span></span>
          </label>
          <label className="text-[11px] text-[#7A7568]">
            Soglia bozze
            <input
              data-testid="sentinel-min-relevance"
              type="number" min={0} max={100} defaultValue={settings.min_relevance}
              onBlur={(e) => { const v = Number(e.target.value); if (v !== settings.min_relevance) saveSettings({ min_relevance: v }); }}
              className="ml-2 w-16 bg-[#111] border border-[#2a2a2a] px-2 py-1 text-[12.5px] text-[#C4A671] focus:border-[#C4A671] outline-none"
            />
            <span className="ml-1">/100 (sotto → solo segnalazione)</span>
          </label>
        </div>
      )}

      {running && run && (
        <div className="mt-4 border border-[#C4A671]/30 bg-[#0F0E0B] px-4 py-3 text-[11.5px] text-[#8B7A55]"
             data-testid="sentinel-run-status">
          Trovati {run.found} · nuovi {run.new} · valutati {run.evaluated}
          {run.current && <span className="italic"> — «{run.current}»</span>}
        </div>
      )}
      {!running && run?.finished_at && (
        <p className="mt-3 text-[11px] text-[#5a5548]">
          Ultima scansione ({run.source === "scheduled" ? "automatica" : "manuale"}):{" "}
          {new Date(run.finished_at).toLocaleString("it-IT")} — {run.new} nuovi, {run.drafted} bozze.
        </p>
      )}

      <div className="mt-6 flex gap-2" data-testid="sentinel-filters">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            data-testid={`sentinel-filter-${key}`}
            onClick={() => { setFilter(key); loadThreads(key); }}
            className={`px-4 py-2 text-[9.5px] uppercase tracking-[0.2em] border transition ${
              filter === key
                ? "border-[#C4A671] text-[#C4A671]"
                : "border-[#2a2a2a] text-[#7A7568] hover:text-[#C9C3B5]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-3" data-testid="sentinel-threads">
        {threads.length === 0 && (
          <p className="text-[12.5px] text-[#5a5548] py-8 text-center border border-dashed border-[#1f1f1f]">
            Nessun thread in questa vista. Lancia una scansione o attendi il prossimo ciclo automatico.
          </p>
        )}
        {threads.map((t) => (
          <ThreadCard key={t.id} t={t} onPatch={patchThread} onRedraft={redraft} onToRadar={toRadar} busyId={busyId} />
        ))}
      </div>
    </div>
  );
}
